/**
 * CollarWorks Selection Page engine — ALL decision logic, ZERO DOM.
 *
 * Nothing about any product is hardcoded here. Everything comes from the projected
 * selection_spec (build/selection-specs/<handle>.json, shipped as custom.selection_spec).
 * The 66-line if-ladder in the retired configurator is exactly what spec.rules[] replaces.
 *
 * WHY THIS FILE HAS NO DOM: it is the same split that made the Phase 1 projector trustworthy.
 * Pure functions can be driven in Node against the real specs, in CI, with no browser and no
 * store — see scripts/test-selection-engine.mjs. assets/cw-selection-page.js is a thin adapter
 * that reads inputs and writes text; if you find yourself adding logic there, it belongs here.
 *
 * VALUE IDENTITY (vocabulary v6): every enumerable value is {id, label, shopify_value}.
 *   state and rules key on  id            — stable, never shown, never changes
 *   the DOM shows           label         — free to rename without breaking anything
 *   Shopify receives        shopify_value — must match the variant/property exactly
 * A comparison against an English word anywhere in this file is a bug.
 *
 * Classic script (Dawn uses no modules). Exposes window.CWSelectionEngine, with a CommonJS
 * tail so Node can require() it.
 */
(function (root) {
  'use strict';

  // ---------------------------------------------------------------------------
  // helpers
  // ---------------------------------------------------------------------------

  var MULTI_SELECT = { swatch_grid_multi: true };

  /** Fields the customer never fills in: they render as copy or carry a constant. */
  function isInput(field) {
    // A derived field (v9) is set by a derive_range rule, never by the customer: it is not
    // validated as an input and sends no property, but it still drives its variant option.
    return field.type !== 'notice' && field.type !== 'fixed' && field.derived !== true;
  }

  /** A field holds an array of ids when it caps selections; otherwise a single id. */
  function isMulti(field) {
    return typeof field.max_selections === 'number';
  }

  function valueById(field, id) {
    var values = field.values || [];
    for (var i = 0; i < values.length; i += 1) if (values[i].id === id) return values[i];
    return null;
  }

  function optionByName(spec, name) {
    var options = (spec.variants && spec.variants.options) || [];
    for (var i = 0; i < options.length; i += 1) if (options[i].name === name) return options[i];
    return null;
  }

  /** Resolve the {id,label,shopify_value} set a field draws from — its own, or its option's. */
  function valuesFor(spec, fieldId, sources) {
    var field = spec.fields[fieldId];
    if (!field) return [];
    if (field.type === 'variant_option') {
      var opt = optionByName(spec, field.option);
      return opt ? opt.values : [];
    }
    if (field.values) return field.values;
    // swatch_grid / select read their values from a metafield or metaobject at runtime.
    if (field.source && sources) return leadFirst(field, sources[field.source] || []);
    return [];
  }

  /**
   * lead_values (v8): pin the declared ids to the FRONT of a picker, in the declared order,
   * with everything else behind them in source order. Ids the source does not know are
   * ignored here — the build has already refused them. The Liquid picker applies the same
   * rule server-side (snippets/cw-swatch-grid.liquid), so the DOM and this list agree.
   */
  function leadFirst(field, values) {
    var leads = field.lead_values;
    if (!leads || !leads.length) return values;
    var front = [];
    leads.forEach(function (id) {
      var match = values.filter(function (v) { return v.id === id; })[0];
      if (match && front.indexOf(match) === -1) front.push(match);
    });
    if (!front.length) return values;
    return front.concat(values.filter(function (v) { return front.indexOf(v) === -1; }));
  }

  /**
   * Preview text rule (Sara, 2026-08-24): when the dog's name is 4–7 characters the phone
   * number is centred on and tracked out to the NAME's width, so the two read as one block.
   * Shorter or longer names leave the phone at its natural width. Lives here, not in the
   * renderer, so the boundary is unit-tested rather than eyeballed.
   */
  function phoneMatchesName(name) {
    var n = String(name || '').length;
    return n >= 4 && n <= 7;
  }

  /**
   * What a numeric pick currently derives (v9): for the field a derive_range rule reads FROM,
   * the {field, value} of the bucket it set — e.g. neck_size (Head Size) 13.5 in -> {field: 'size',
   * value: {id: 'm', label: 'M', ...}} — or null when nothing is derived yet. The page shows
   * it as "Sized as M" under the dropdown.
   */
  function derivedFrom(spec, state, fromFieldId) {
    var rule = (spec.rules || []).filter(function (r) { return r.type === 'derive_range' && r.from === fromFieldId; })[0];
    if (!rule) return null;
    var target = spec.fields[rule.field];
    if (!target) return null;
    var values = valuesFor(spec, rule.field);
    var current = state[rule.field];
    var match = values.filter(function (v) { return v.id === current; })[0];
    return match ? { field: rule.field, value: match } : null;
  }

  var EXTRAS_OPTION = 'Extras';

  /**
   * The derived Extras option, if this product has one.
   *
   * ⚠️ Unlike every other variant option, Extras is driven by NO single field. It is generated
   * from all the `priced_by: "variant_extra"` toggles at build time (scripts/lib/spec-extras.mjs)
   * because Shopify prices a cart line from its variant and the only overrides — Cart Transform
   * and Scripts — are Plus-only. Each value carries a `fields` map, which is the bridge in both
   * directions: toggles -> variant when adding to cart, variant -> toggles when rehydrating.
   */
  function extrasOption(spec) {
    return ((spec.variants && spec.variants.options) || [])
      .filter(function (o) { return o.name === EXTRAS_OPTION; })[0] || null;
  }

  /** Which Extras value matches the customer's current toggles? */
  function matchExtras(option, state) {
    if (!option) return null;
    for (var i = 0; i < option.values.length; i += 1) {
      var value = option.values[i];
      var fields = value.fields || {};
      var ok = true;
      for (var key in fields) {
        if (Object.prototype.hasOwnProperty.call(fields, key) && state[key] !== fields[key]) ok = false;
      }
      if (ok) return value;
    }
    return null;
  }

  // ---------------------------------------------------------------------------
  // state
  /** The child ids a metaobject-driven dependency allows right now, in catalogue order. */
  function metaobjectAllowedIds(spec, rule, state, sources) {
    var parentField = spec.fields[rule.depends_on];
    if (!parentField || !sources) return [];
    var listKey = String(rule.options_from || '').split('.')[1] || 'designs';
    var entries = sources[parentField.source] || [];
    var chosen = state[rule.depends_on];
    var entry = entries.filter(function (e) { return e.id === chosen; })[0];
    return (entry && entry[listKey]) || [];
  }

  /**
   * What the UI should OFFER for a field right now: valuesFor, narrowed and ordered by any
   * dependency rule. dependent_values keeps the spec map's order; the metaobject flavour
   * keeps the catalogue's authored list order. applyRules keeps STATE legal; this keeps the
   * OPTION LIST honest — both must agree or the customer can see a choice they cannot make.
   */
  function optionsFor(spec, fieldId, state, sources) {
    var all = valuesFor(spec, fieldId, sources);
    (spec.rules || []).forEach(function (rule) {
      if (rule.field !== fieldId) return;
      if (rule.type === 'dependent_values') {
        var allowed = (rule.map || {})[state[rule.depends_on]] || [];
        if (allowed.length) {
          all = allowed.map(function (id) {
            return all.filter(function (v) { return v.id === id; })[0];
          }).filter(Boolean);
        }
      }
      if (rule.type === 'dependent_values_from_metaobject') {
        var ids = metaobjectAllowedIds(spec, rule, state, sources);
        all = ids.map(function (id) {
          return all.filter(function (v) { return v.id === id; })[0];
        }).filter(Boolean);
        // The design sheet in the stage numbers its designs in this exact order, so the
        // dropdown says "3 · Wildflower Fields" to let the customer match the two. Only
        // the LABEL is numbered — id and shopify_value are untouched, so the cart property
        // and every lookup by id still see the plain name. Copy, never mutate: `all` holds
        // the shared source objects.
        all = all.map(function (v, i) {
          var copy = {};
          Object.keys(v).forEach(function (k) { copy[k] = v[k]; });
          copy.label = (i + 1) + ' · ' + v.label;
          return copy;
        });
      }
    });
    return all;
  }

  // ---------------------------------------------------------------------------

  /**
   * Starting state: every field's declared default, nothing else.
   * A metaobject-sourced group field (Season, Book) starts NULL on purpose (v10): while no
   * group is chosen the stage shows every active group's design sheet, and the old
   * `default_from: metaobject:is_default` preselect that skipped that state is retired.
   */
  function initialState(spec) {
    var state = {};
    Object.keys(spec.fields || {}).forEach(function (id) {
      var field = spec.fields[id];
      if (!isInput(field)) return;
      if (isMulti(field)) { state[id] = []; return; }
      if (field.default !== undefined && field.default !== null) { state[id] = field.default; return; }
      state[id] = null;
    });
    return state;
  }

  // ---------------------------------------------------------------------------
  // rules
  // ---------------------------------------------------------------------------

  function conditionHolds(cond, state) {
    if (!cond || !cond.field) return false;
    var value = state[cond.field];
    if (cond.is_set === true) {
      return Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== '';
    }
    return value === cond.equals;
  }

  /**
   * Which fields are on the page right now. A field not listed by any show_if is always
   * visible; a field listed by one is visible only while its condition holds.
   */
  function visibleFields(spec, state) {
    var gated = {};
    (spec.rules || []).forEach(function (rule) {
      if (rule.type !== 'show_if') return;
      (rule.fields || []).forEach(function (id) {
        // A field gated by several rules needs all of them; none exist today, but || would
        // silently pick the wrong semantics if one appeared.
        gated[id] = (gated[id] === undefined ? true : gated[id]) && conditionHolds(rule.when, state);
      });
    });
    var visible = {};
    Object.keys(spec.fields || {}).forEach(function (id) {
      // Non-input fields (notice, fixed) are included too: a show_if can gate a NOTICE —
      // the shirt's follow-up note appears only once a design is chosen — and skipping
      // them here silently pinned such notices to their initial Liquid state forever.
      visible[id] = gated[id] === undefined ? true : gated[id];
    });
    return visible;
  }

  /**
   * Enforce the rules that CHANGE state rather than just hide things.
   * Returns { state, notice } — notice is the customer-facing message when a
   * mutually_exclusive rule fired, so the UI can explain why something flipped.
   */
  function applyRules(spec, state, changedField, memory, sources) {
    var next = {};
    Object.keys(state).forEach(function (k) {
      next[k] = Array.isArray(state[k]) ? state[k].slice() : state[k];
    });
    var remembered = {};
    Object.keys(memory || {}).forEach(function (k) {
      remembered[k] = Array.isArray(memory[k]) ? memory[k].slice() : memory[k];
    });
    var notice = null;
    var parkedNow = {};

    (spec.rules || []).forEach(function (rule) {
      if (rule.type === 'mutually_exclusive') {
        if (!conditionHolds(rule.a, next) || !conditionHolds(rule.b, next)) return;
        // Whichever side the customer just touched wins; the other side resets to its default.
        var loser = changedField === rule.a.field ? rule.b : rule.a;
        var field = spec.fields[loser.field];
        var losing = next[loser.field];
        if (field && losing !== field.default && losing !== null && losing !== undefined) {
          remembered[loser.field] = Array.isArray(losing) ? losing.slice() : losing;
          parkedNow[loser.field] = true;
        }
        next[loser.field] = field && field.default !== undefined ? field.default : null;
        notice = rule.message || null;
      }

      if (rule.type === 'max_selections') {
        var picked = next[rule.field];
        if (Array.isArray(picked) && picked.length > rule.max) {
          next[rule.field] = picked.slice(0, rule.max);
        }
      }

      if (rule.type === 'dependent_values') {
        var parentId = next[rule.depends_on];
        var allowed = (rule.map || {})[parentId] || [];
        // RESET RULE (vocabulary v6): when the current value is no longer valid, reset to the
        // FIRST value in the new list. Every map array is ordered cheapest-first and the build
        // asserts it, which is what makes "first" and "cheapest" the same rule.
        if (allowed.length && allowed.indexOf(next[rule.field]) === -1) {
          next[rule.field] = allowed[0];
        }
      }

      if (rule.type === 'derive_range') {
        // v9: the derived field (Slip's Size) follows the numeric pick (head size) through the
        // id -> bucket map the build projected from the authored ranges. No number parsing
        // here — a value id either maps or the field is unset.
        var fromId = next[rule.from];
        next[rule.field] = (fromId && rule.map && rule.map[fromId]) || null;
      }

      if (rule.type === 'dependent_values_from_metaobject') {
        // Same shape as dependent_values, but the map lives in the CATALOGUE: options_from
        // "season.designs" means "the designs list of the chosen season entry". The list is
        // ordered as Sara authored it, so the same RESET RULE lands on the group's lead
        // design. (Considered resetting to null to force a fresh pick; reset-to-first is the
        // v6 unified rule and keeps the page valid — flag for Ed if the UX should differ.)
        var moIds = metaobjectAllowedIds(spec, rule, next, sources);
        if (moIds.length && moIds.indexOf(next[rule.field]) === -1) {
          next[rule.field] = moIds[0];
        }
      }
    });

    /**
     * Hidden fields are PARKED, not erased.
     *
     * A hidden field must not reach the cart or pick a variant — a collar with Splatter hidden
     * must not resolve to a Splatter variant. But throwing the value away means a customer who
     * flips Personalized on and back off loses everything they typed, which is the single most
     * annoying thing a configurator can do. So the effective value falls back to the default
     * while the real one is kept in `memory` and restored the moment the field returns.
     */
    for (var pass = 0; pass < 4; pass += 1) {
    var before = JSON.stringify(next);
    var visible = visibleFields(spec, next);
    Object.keys(next).forEach(function (id) {
      var field = spec.fields[id];
      if (!field) return;

      if (!visible[id]) {
        // Park whatever they had, unless it is already the empty/default value — remembering
        // "nothing" would only stop a later real value from being restored.
        var current = next[id];
        var worthKeeping = Array.isArray(current)
          ? current.length > 0
          : (current !== null && current !== undefined && current !== '' && current !== field.default);
        if (worthKeeping) remembered[id] = Array.isArray(current) ? current.slice() : current;
        if (isMulti(field)) next[id] = [];
        else if (field.default !== undefined && field.default !== null) next[id] = field.default;
        else next[id] = null;
        return;
      }

      // INVARIANT: memory holds values only for fields that are currently HIDDEN. A field that
      // is visible has no business having a parked value, so anything left over from this pass —
      // for instance a style that a mutually_exclusive rule just reset while style stayed on
      // screen — is dropped rather than restored. Without this, choosing Splatter would reset
      // Personalized and then immediately put it back.
      if (Object.prototype.hasOwnProperty.call(remembered, id)) {
        var back = remembered[id];
        var isEmpty = Array.isArray(next[id])
          ? next[id].length === 0
          : (next[id] === null || next[id] === undefined || next[id] === '' || next[id] === field.default);
        // Only restore a value that was parked by a PREVIOUS call. Within this call the rule
        // phase may have reset a still-visible field on purpose.
        if (isEmpty && back !== undefined && back !== null && !parkedNow[id]) {
          next[id] = Array.isArray(back) ? back.slice() : back;
        }
        delete remembered[id];
      }
    });
    if (JSON.stringify(next) === before) break;
    }

    return { state: next, notice: notice, memory: remembered };
  }

  // ---------------------------------------------------------------------------
  // validation
  // ---------------------------------------------------------------------------

  /**
   * ⚠️ "Required" means required AND CURRENTLY VISIBLE.
   * text_color is required:true but only shown when style == personalized. Without this
   * scoping an unpersonalized collar could never be added to the cart.
   */
  function validate(spec, state) {
    var visible = visibleFields(spec, state);
    var problems = [];
    Object.keys(spec.fields || {}).forEach(function (id) {
      var field = spec.fields[id];
      if (!isInput(field) || !field.required || !visible[id]) return;
      var value = state[id];
      var empty = Array.isArray(value) ? value.length === 0 : value === null || value === undefined || value === '';
      if (empty) {
        problems.push({ field: id, label: field.label || id, message: 'Pick a ' + String(field.label || id).toLowerCase() + ' to continue.' });
      }
    });
    return problems;
  }

  // ---------------------------------------------------------------------------
  // variants and price
  // ---------------------------------------------------------------------------

  /** The shopify_value for each variant option, in the spec's option order. */
  function selectedOptionValues(spec, state) {
    var options = (spec.variants && spec.variants.options) || [];
    return options.map(function (opt) {
      if (opt.name === EXTRAS_OPTION) {
        var match = matchExtras(opt, state);
        return match ? match.shopify_value : null;
      }
      var fieldId = null;
      Object.keys(spec.fields).forEach(function (id) {
        if (spec.fields[id].type === 'variant_option' && spec.fields[id].option === opt.name) fieldId = id;
      });
      var value = valueById({ values: opt.values }, state[fieldId]);
      return value ? value.shopify_value : null;
    });
  }

  /** Match against Shopify's own variant list. Never matches on a display label. */
  function resolveVariant(spec, state, variants) {
    var wanted = selectedOptionValues(spec, state);
    if (wanted.indexOf(null) !== -1) return null;
    for (var i = 0; i < (variants || []).length; i += 1) {
      var v = variants[i];
      var have = [v.option1, v.option2, v.option3].slice(0, wanted.length);
      var match = true;
      for (var j = 0; j < wanted.length; j += 1) if (have[j] !== wanted[j]) match = false;
      if (match) return v;
    }
    return null;
  }

  // ---------------------------------------------------------------------------
  // variant pages
  // ---------------------------------------------------------------------------

  /**
   * Liquid's `product.variants` stops at 250 (so does /products/<handle>.js), and Two-Tone has
   * 320. The section ships the first page inline; the rest come from the cw-variants-page
   * section, one `paginate` page each. These two functions are the arithmetic of that, kept
   * here so test:engine proves them without a browser.
   */
  var VARIANT_PAGE_SIZE = 250;

  /** Page numbers still to fetch: [] when the inline list already holds every variant. */
  function variantPagesToFetch(inlineCount, total, pageSize) {
    var size = pageSize || VARIANT_PAGE_SIZE;
    if (inlineCount > total) {
      throw new Error('inline variant list has ' + inlineCount + ' but the product counts ' + total);
    }
    if (total === inlineCount) return [];
    if (inlineCount !== Math.min(total, size)) {
      throw new Error('inline variant list has ' + inlineCount + ' of ' + total +
        ' but should hold the first ' + Math.min(total, size));
    }
    var pages = [];
    for (var n = 2; n <= Math.ceil(total / size); n += 1) pages.push(n);
    return pages;
  }

  /**
   * The full variant list, or a throw. Never a partial list: a missing variant is a combination
   * the customer can pick and not buy, which is the bug this exists to fix.
   * `payloads[i]` answers `pages[i]` and is the cw-variants-page JSON:
   * { page, page_size, items, variants }.
   */
  function mergeVariantPages(inline, payloads, pages, total, pageSize) {
    var size = pageSize || VARIANT_PAGE_SIZE;
    var all = (inline || []).slice();
    if ((payloads || []).length !== pages.length) {
      throw new Error('expected ' + pages.length + ' variant pages, got ' + (payloads || []).length);
    }
    pages.forEach(function (n, i) {
      var p = payloads[i] || {};
      if (p.page !== n) throw new Error('asked for variant page ' + n + ', got page ' + p.page);
      if (p.page_size !== size) throw new Error('variant page ' + n + ' has page_size ' + p.page_size + ', expected ' + size);
      if (p.items !== total) throw new Error('variant page ' + n + ' counts ' + p.items + ' variants, expected ' + total);
      if (!Array.isArray(p.variants)) throw new Error('variant page ' + n + ' has no variants list');
      all = all.concat(p.variants);
    });
    if (all.length !== total) throw new Error('loaded ' + all.length + ' of ' + total + ' variants');
    var seen = {};
    all.forEach(function (v) {
      var id = String(v && v.id);
      if (seen[id]) throw new Error('variant ' + id + ' appears twice');
      seen[id] = true;
    });
    return all;
  }

  /** Base + variant option deltas, used when Shopify's real variant price is unavailable. */
  function variantPriceCents(spec, state) {
    var options = (spec.variants && spec.variants.options) || [];
    var total = spec.base_price_cents || 0;
    options.forEach(function (opt) {
      var fieldId = null;
      Object.keys(spec.fields).forEach(function (id) {
        if (spec.fields[id].type === 'variant_option' && spec.fields[id].option === opt.name) fieldId = id;
      });
      var id = state[fieldId];
      if (opt.name === EXTRAS_OPTION) {
        var extra = matchExtras(opt, state);
        if (extra) total += (opt.price_delta_cents || {})[extra.id] || 0;
        return;
      }
      if (!id) return;
      if (opt.price_delta_cents_by_style) {
        var styleField = null;
        Object.keys(spec.fields).forEach(function (f) {
          if (spec.fields[f].type === 'variant_option' && spec.fields[f].option === 'Style') styleField = f;
        });
        var byStyle = opt.price_delta_cents_by_style[state[styleField]] || {};
        total += byStyle[id] || 0;
      } else {
        total += (opt.price_delta_cents || {})[id] || 0;
      }
    });
    return total;
  }

  /**
   * What the customer is told. The variant price is authoritative when Shopify gave us one;
   * every priced_by:cart_transform delta is added on top, because the function applies those in
   * the cart and the page must not silently disagree with what they will be charged.
   */
  function estimateCents(spec, state, knownVariantPriceCents) {
    // ⚠️ NOTHING is added on top of the variant price any more. The add-ons live in the derived
    // Extras variant option, so the variant IS the full price. The previous version added
    // `priced_by: cart_transform` deltas here to mirror what the function would charge in the
    // cart — but that function is Shopify Plus only and never charged anything on Basic, so the
    // page was quoting a price the customer would never actually be billed.
    return typeof knownVariantPriceCents === 'number'
      ? knownVariantPriceCents
      : variantPriceCents(spec, state);
  }

  /**
   * The bundle discount rate for this product's add-on, as a percentage. 0 when there is none.
   *
   * ⚠️ ONE SOURCE. The rate lives in the spec (`addons[].pricing.bundle_discount_percent`) and
   * is mirrored by DEFAULT_PERCENT in the leash-bundle-discount function. scripts/test-addon-
   * discount.mjs asserts the two agree — the page must never advertise a saving the checkout
   * will not give.
   */
  function addonDiscountPercent(spec) {
    var addon = (spec.addons || [])[0];
    var pct = addon && addon.pricing && addon.pricing.bundle_discount_percent;
    return typeof pct === 'number' && pct > 0 ? pct : 0;
  }

  /**
   * What the add-on actually costs once the bundle discount lands.
   *
   * The discount is applied by a Shopify Discount Function at checkout, not by us; this is the
   * page's honest forecast of it. Rounding is half-up on the final price, which is what Shopify
   * does for a percentage discount — a cent of disagreement on an odd price is possible and is
   * why the page says "Estimated total".
   */
  function addonPriceCents(spec, fullCents) {
    var pct = addonDiscountPercent(spec);
    if (!pct || typeof fullCents !== 'number') return fullCents;
    return Math.round((fullCents * (100 - pct)) / 100);
  }

  // ---------------------------------------------------------------------------
  // cart
  // ---------------------------------------------------------------------------

  /**
   * Line item properties for one product.
   *
   * ⚠️ Markers are matched by the Cart Transform on their EXACT value — an unchecked box sends
   *    "No", so a marker must be sent with its real value, never omitted and never blank.
   * ⚠️ A property is never sent as an empty string. Blank properties are noise on the order and
   *    on the packing slip.
   *
   * HIDDEN VALUE (v13, 2026-10-10). A field a show_if has hidden normally sends nothing. One
   * that declares hidden_value sends hidden_value.shopify_value instead: on Seasonal and Bookish
   * 'Default Colors' hides the swatches, and Sara still needs the order to say "Base Layer
   * Color: Default" rather than say nothing, which reads like a lost pick. The add-on line
   * inherits through this same function (cartItems), so the leash says "Default" too with no
   * add-on code of its own. A field without hidden_value is unchanged: still nothing.
   */
  function propertiesFor(spec, state, sources) {
    var visible = visibleFields(spec, state);
    var props = {};

    Object.keys(spec.fields || {}).forEach(function (id) {
      var field = spec.fields[id];
      if (!field.property_key) return;

      if (field.type === 'fixed') {
        if (field.value) props[field.property_key] = field.value.shopify_value;
        return;
      }
      if (!isInput(field)) return;
      if (!visible[id]) {
        if (field.hidden_value) props[field.property_key] = field.hidden_value.shopify_value;
        return;
      }

      var raw = state[id];
      if (raw === null || raw === undefined || raw === '') return;

      if (Array.isArray(raw)) {
        if (!raw.length) return;
        var labels = raw.map(function (vid) {
          var v = valueById({ values: valuesFor(spec, id, sources) }, vid);
          return v ? v.shopify_value : vid;
        });
        props[field.property_key] = labels.join(', ');
        return;
      }

      var values = valuesFor(spec, id, sources);
      if (values.length) {
        var match = valueById({ values: values }, raw);
        props[field.property_key] = match ? match.shopify_value : raw;
      } else {
        // Freeform text: trim, and drop it entirely if that leaves nothing.
        var text = String(raw).trim();
        if (text) props[field.property_key] = text;
      }
    });

    return props;
  }

  /**
   * The /cart/add.js items[] array. One entry for the product, plus one for an opted-in add-on
   * so a single call produces two distinct cart lines.
   */
  function cartItems(spec, state, context) {
    var ctx = context || {};
    var items = [];
    var variant = ctx.variant || resolveVariant(spec, state, ctx.variants || []);
    if (!variant) return items;

    items.push({
      id: variant.id,
      quantity: 1,
      properties: propertiesFor(spec, state, ctx.sources),
    });

    var addon = (spec.addons || [])[0];
    if (addon && ctx.addon && ctx.addon.enabled && ctx.addon.variant) {
      var addonProps = {};
      // Inherit names the PARENT's own field ids — the add-on shows those colours without
      // asking again, so they must carry the parent's property values.
      (addon.inherit || []).forEach(function (parentFieldId) {
        var parentField = spec.fields[parentFieldId];
        if (!parentField) return;
        if (parentField.property_key) {
          var parentProps = propertiesFor(spec, state, ctx.sources);
          var v = parentProps[parentField.property_key];
          if (v) addonProps[ctx.addon.propertyKeyFor ? ctx.addon.propertyKeyFor(parentFieldId) : parentField.property_key] = v;
          return;
        }
        // An inherited variant_option (the parent's Width) has NO property key — the parent's
        // variant title carries it. The ADD-ON line still needs it stated, or the order never
        // says what width the traffic handle itself must be made in (requirements §8: colours
        // AND width inherit). It rides under the option's display name, e.g. "Width: 3/4 in".
        if (parentField.type === 'variant_option' && parentField.option) {
          var chosen = state[parentFieldId];
          var match = (valuesFor(spec, parentFieldId) || []).filter(function (x) { return x.id === chosen; })[0];
          if (match) addonProps[parentField.option] = match.shopify_value;
        }
      });
      Object.keys(ctx.addon.properties || {}).forEach(function (k) {
        if (ctx.addon.properties[k]) addonProps[k] = ctx.addon.properties[k];
      });
      // ⚠️ THE MARKER THE DISCOUNT FUNCTION MATCHES ON. Its value is the parent's handle, so
      // an order also records WHICH product the add-on was bought with. Underscore-prefixed,
      // so Dawn hides it from the customer while the admin and the function both still see it.
      // Without it the leash is indistinguishable from one bought on its own and is charged
      // full price — which is exactly the bug this replaced.
      //
      // ⚠️ ONLY when this add-on actually has a bundle rate (bundle_discount_percent > 0).
      // Since 2026-08-23 the traffic handle IS in the 15% bundle too, so today every add-on
      // carries a rate — but the guard stays: a future rate-0 add-on marked _addon_of would
      // be discounted by a function that has no idea it was never meant to be included.
      if (addonDiscountPercent(spec) > 0) addonProps._addon_of = spec.handle;
      items.push({ id: ctx.addon.variant.id, quantity: 1, properties: addonProps });
    }

    return items;
  }

  /**
   * Restore state from a cart line — the cart's Edit link.
   *
   * TWO sources, because a configured collar is stored in two places:
   *   - line item PROPERTIES carry the colours, text and markers
   *   - the VARIANT carries size and width
   * variant_option fields have no property_key precisely because they are the variant, so
   * properties alone can never restore them. Pass the line's variant (option1/option2/option3,
   * as /cart.js returns it) or the Edit link silently drops the size.
   *
   * The property side reads the SAME property_key map propertiesFor writes, so the restore
   * path and the submit path cannot drift apart.
   *
   * GATES (v13, 2026-10-10). A show_if gate such as Seasonal's Default/Pick Colors buttons has
   * no property_key, so no order records it. After the property pass, inferGates rebuilds it
   * from what WAS recovered: real colours mean the gate was open (Pick). "Default" is not a
   * palette value, so it is dropped here, never counts as recovered, and the gate keeps its
   * default. An order placed before the buttons existed (real colours, no toggle) rebuilds as
   * Pick, which is what that customer actually did.
   */
  function hydrate(spec, properties, sources, variant) {
    var state = initialState(spec);

    // Variant options first — they are required, and nothing else depends on them.
    if (variant) {
      var options = (spec.variants && spec.variants.options) || [];
      options.forEach(function (opt, index) {
        var shopifyValue = variant['option' + (index + 1)];
        if (!shopifyValue) return;
        var fieldId = null;
        Object.keys(spec.fields).forEach(function (id) {
          if (spec.fields[id].type === 'variant_option' && spec.fields[id].option === opt.name) fieldId = id;
        });
        if (opt.name === EXTRAS_OPTION) {
          // The Extras value carries the toggles that produced it — this is why these fields
          // need no line item property of their own.
          for (var k = 0; k < opt.values.length; k += 1) {
            if (opt.values[k].shopify_value !== shopifyValue) continue;
            var fields = opt.values[k].fields || {};
            Object.keys(fields).forEach(function (f) { state[f] = fields[f]; });
            return;
          }
          return;
        }
        if (!fieldId) return;
        for (var i = 0; i < opt.values.length; i += 1) {
          if (opt.values[i].shopify_value === shopifyValue) { state[fieldId] = opt.values[i].id; return; }
        }
      });
    }

    if (!properties) return state;

    // Only ids whose value actually landed in state: inferGates must not read a field's
    // default as evidence that the order said anything about it.
    var recovered = [];

    Object.keys(spec.fields || {}).forEach(function (id) {
      var field = spec.fields[id];
      if (!field.property_key || !isInput(field)) return;
      var raw = properties[field.property_key];
      if (raw === undefined || raw === null || raw === '') return;

      var values = valuesFor(spec, id, sources);

      if (isMulti(field)) {
        state[id] = String(raw).split(',').map(function (s) { return s.trim(); })
          .filter(Boolean)
          .map(function (shopifyValue) {
            for (var i = 0; i < values.length; i += 1) {
              if (values[i].shopify_value === shopifyValue) return values[i].id;
            }
            return null;
          })
          .filter(Boolean);
        if (state[id].length) recovered.push(id);
        return;
      }

      if (values.length) {
        for (var i = 0; i < values.length; i += 1) {
          if (values[i].shopify_value === raw) { state[id] = values[i].id; recovered.push(id); return; }
        }
        return; // an unrecognised value is dropped rather than poisoning state
      }
      state[id] = raw;
      recovered.push(id);
    });

    return inferGates(spec, state, recovered, sources);
  }

  /**
   * Rebuild show_if gates that no source recorded (v13, 2026-10-10). Pure: returns a NEW state
   * and never mutates `state`.
   *
   * The Default/Pick buttons on Seasonal and Bookish have no property_key (the order already
   * says "Default" or the real colour), so an order or a session saved before they existed
   * cannot say which button was pressed. The fields they gate can: a field that declares
   * hidden_value and came back with a REAL value (one of its values, never the hidden_value)
   * could only have been picked while its gate was open, so the gate is set to when.equals.
   *
   * `known` lists the field ids whose value was recovered from a source (order properties or a
   * saved session). A gate in `known` was recorded and is left exactly as recorded; a field not
   * in `known` is only its default and proves nothing.
   *
   * Only an `equals` gate with its own values qualifies. A variant_option or a variant_extra
   * toggle comes back from the variant itself, and no gate on any other spec gates a
   * hidden_value field, so for those this returns an unchanged copy.
   */
  function inferGates(spec, state, known, sources) {
    var next = copyState(state);
    var knownIds = Array.isArray(known) ? known : [];
    ((spec && spec.rules) || []).forEach(function (rule) {
      if (rule.type !== 'show_if' || !rule.when) return;
      if (rule.when.is_set === true || typeof rule.when.equals !== 'string') return;
      var gate = rule.when.field;
      var g = spec.fields[gate];
      if (!g || !Array.isArray(g.values)) return;
      if (g.type === 'variant_option' || g.priced_by === 'variant_extra') return;
      if (knownIds.indexOf(gate) !== -1) return;

      var opened = (rule.fields || []).some(function (fid) {
        var f = spec.fields[fid];
        if (!f || !f.hidden_value || knownIds.indexOf(fid) === -1) return false;
        var v = state[fid];
        if (typeof v !== 'string' || v === '' || v === f.hidden_value.id) return false;
        return valuesFor(spec, fid, sources).some(function (x) { return x.id === v; });
      });
      if (opened) next[gate] = rule.when.equals;
    });
    return next;
  }

  // ---------------------------------------------------------------------------
  // links (?<group>=...&<design>=...)
  // ---------------------------------------------------------------------------

  /**
   * The one slugifier. Identical to scripts/lib/spec-schema.mjs slugify, which mints every
   * value id at build time; test:engine binds the two together so a link, a catalogue entry
   * and a build id can never slug the same label differently. The page delegates to this.
   */
  function slug(label) {
    return String(label)
      .toLowerCase()
      .replace(/[/.\s_]+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /** A raw param value that is missing or only whitespace counts as no param at all. */
  function linkValue(params, name) {
    var raw = params && typeof params.get === 'function' ? params.get(name) : null;
    if (raw === null || raw === undefined || String(raw).trim() === '') return null;
    return { raw: String(raw), norm: slug(raw) };
  }

  /** First entry, in list order, whose id or slugged handle equals the normalised value. */
  function matchEntry(entries, norm) {
    for (var i = 0; i < entries.length; i += 1) {
      var e = entries[i];
      if (!e || e.active === false) continue;
      if (typeof e.handle === 'string' && e.handle !== '' && slug(e.handle) === norm) return e;
      if (e.id === norm) return e;
    }
    return null;
  }

  function copyState(state) {
    var next = {};
    Object.keys(state || {}).forEach(function (k) {
      next[k] = Array.isArray(state[k]) ? state[k].slice() : state[k];
    });
    return next;
  }

  /**
   * Read a season/book link and decide what it picks. Pure: no window, no DOM, never mutates
   * `state`, never calls applyRules (the page's settle pass runs right after and lands a null
   * design on the group's first design, exactly as for a fresh visitor).
   *
   * Param names come from the spec, never from code: every dependent_values_from_metaobject
   * rule makes `depends_on` the group param and `field` the design param. Any future product
   * built as a chained dropdown gets links for free, and no group or design name lives here.
   *
   * Ed's decisions, 2026-10-09:
   *   1a  An unknown, inactive or expired value is IGNORED: the page behaves as if the param
   *       were absent. Ignored non-empty values come back as warnings so the page can log them;
   *       an empty value is not a mistake worth a warning.
   *   2a  The link wins when the shopper ARRIVES, even over a restored session, but only the
   *       group and design change. A shopper's own later pick survives a reload or Back: on
   *       those navigation types a restored session wins. With no restored session (they never
   *       touched anything) the link applies again, and so it does when `context.untouched`
   *       says the link applied last time and nothing has changed since. An unreadable
   *       navigation type is an arrival.
   *   3b  `&<design>=` preselects a design INSIDE the linked group. A design outside the group,
   *       or with no/unknown group, is not guessed at: the group (if any) still applies and the
   *       design falls to the group's first.
   * A cart Edit link (`context.editing`) is an explicit instruction and outranks any link.
   *
   * @param params  anything with .get(name) -> string|null (URLSearchParams)
   * @param context {navigation, restored, editing, untouched}
   * @returns {state, picks, applied, warnings}  warnings: [{param, value, reason, message}],
   *          reason one of 'unknown' | 'not-in-group' | 'no-group'
   */
  function applyLink(spec, state, params, sources, context) {
    var ctx = context || {};
    var rules = ((spec && spec.rules) || []).filter(function (r) {
      return r.type === 'dependent_values_from_metaobject';
    });
    var noop = { state: copyState(state), picks: {}, applied: false, warnings: [] };
    if (!rules.length || ctx.editing) return noop;

    var picks = {};
    var warnings = [];

    rules.forEach(function (rule) {
      var groupField = rule.depends_on;
      var designField = rule.field;
      var group = linkValue(params, groupField);
      var design = linkValue(params, designField);

      var groupEntry = null;
      if (group) {
        groupEntry = matchEntry(valuesFor(spec, groupField, sources), group.norm);
        if (!groupEntry) {
          warnings.push({
            param: groupField, value: group.raw, reason: 'unknown',
            message: '?' + groupField + '=' + group.raw + ' matches no active ' + groupField + '; ignored',
          });
        }
      }

      if (!groupEntry) {
        if (design) {
          warnings.push({
            param: designField, value: design.raw, reason: 'no-group',
            message: '?' + designField + '=' + design.raw + ' needs a valid ?' + groupField + '=; ignored',
          });
        }
        return;
      }

      // Match against exactly what the Design dropdown OFFERS for this group (optionsFor):
      // the group's authored list narrowed to designs the page actually has. A design that is
      // still listed on its group but no longer active must not reach state through a link
      // when the dropdown itself cannot show it.
      var groupState = {};
      groupState[groupField] = groupEntry.id;
      var designId = null;
      if (design) {
        var offered = matchEntry(optionsFor(spec, designField, groupState, sources), design.norm);
        if (offered) designId = offered.id;
        if (designId === null) {
          warnings.push({
            param: designField, value: design.raw, reason: 'not-in-group',
            message: '?' + designField + '=' + design.raw + ' is not a ' + designField + ' in ' +
              groupField + ' ' + groupEntry.id + '; using its first ' + designField,
          });
        }
      }

      picks[groupField] = groupEntry.id;
      picks[designField] = designId;
    });

    // `untouched`: this same link applied on the tab's previous load and the shopper has
    // changed nothing since. A reload then must not fall back to an OLDER saved session from
    // before the link (a Fall collar configured earlier in the tab, then a Halloween ad).
    var nav = ctx.navigation;
    var isArrival = nav !== 'reload' && nav !== 'back_forward';
    var applied = Object.keys(picks).length > 0 && (!ctx.restored || isArrival || ctx.untouched === true);
    var next = copyState(state);
    if (applied) Object.keys(picks).forEach(function (k) { next[k] = picks[k]; });
    return { state: next, picks: picks, applied: applied, warnings: warnings };
  }

  var CWSelectionEngine = {
    initialState: initialState,
    visibleFields: visibleFields,
    applyRules: applyRules,
    validate: validate,
    resolveVariant: resolveVariant,
    VARIANT_PAGE_SIZE: VARIANT_PAGE_SIZE,
    variantPagesToFetch: variantPagesToFetch,
    mergeVariantPages: mergeVariantPages,
    selectedOptionValues: selectedOptionValues,
    variantPriceCents: variantPriceCents,
    estimateCents: estimateCents,
    addonDiscountPercent: addonDiscountPercent,
    addonPriceCents: addonPriceCents,
    propertiesFor: propertiesFor,
    cartItems: cartItems,
    hydrate: hydrate,
    valuesFor: valuesFor,
    optionsFor: optionsFor,
    phoneMatchesName: phoneMatchesName,
    derivedFrom: derivedFrom,
    extrasOption: extrasOption,
    matchExtras: matchExtras,
    isInput: isInput,
    isMulti: isMulti,
    slug: slug,
    applyLink: applyLink,
    inferGates: inferGates,
  };

  root.CWSelectionEngine = CWSelectionEngine;
  if (typeof module !== 'undefined' && module.exports) module.exports = CWSelectionEngine;
})(typeof window !== 'undefined' ? window : globalThis);
